"use client";

// Reps : déverrouillage à l'ouverture d'une leçon quand on a des CLÉS LIMITÉES.
// La clé dépensée glisse dans la serrure, la tourne, déverrouille ; puis l'overlay
// se lève pour révéler la leçon (le fondu de sortie = la révélation). Le compteur
// tombe de 1. Skippable (un tap passe direct). Version "full" les premières fois,
// "light" (rapide) ensuite. JAMAIS pour les abonnés (illimité = aucune dépense).
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { useSound } from "@/contexts/SoundContext";

const FULL_MS = 1450;

const SPARKS = [
    { dx: -40, dy: -30, s: 8 },
    { dx: 44, dy: -24, s: 6 },
    { dx: 34, dy: 38, s: 7 },
    { dx: -38, dy: 34, s: 6 },
];

export default function RepsKeyUnlock({ remaining, onDone }) {
    const safeRemaining = typeof remaining === "number" ? remaining : 0;
    const [open, setOpen] = useState(true);
    const [count, setCount] = useState(safeRemaining + 1);
    const doneRef = useRef(false);
    const { playSound } = useSound();

    useEffect(() => {
        const soundAt = 780;
        const tickAt = 900;
        const closeAt = FULL_MS;
        const s = setTimeout(
            () => playSound("/static/sounds/reward-key.mp3", { volume: 0.34 }),
            soundAt,
        );
        const tk = setTimeout(() => setCount(safeRemaining), tickAt);
        const c = setTimeout(() => setOpen(false), closeAt);
        return () => {
            clearTimeout(s);
            clearTimeout(tk);
            clearTimeout(c);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function finish() {
        if (doneRef.current) return;
        doneRef.current = true;
        if (onDone) onDone();
    }

    return (
        <AnimatePresence onExitComplete={finish}>
            {open && (
                <motion.div
                    key="reps-key-unlock"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.22 }}
                    onClick={() => setOpen(false)}
                    className="fixed inset-0 z-[9998] flex items-center justify-center"
                    style={{ background: "rgba(24,18,12,0.62)" }}
                >
                    <style>{`
@keyframes rkuKey{0%{transform:translate(46px,-90px) rotate(-12deg);opacity:0}24%{transform:translate(-6px,-44px) rotate(-12deg);opacity:1}48%{transform:translate(-6px,-44px) rotate(30deg);opacity:1}72%{transform:translate(-6px,-44px) rotate(30deg);opacity:1}100%{transform:translate(-6px,-44px) rotate(30deg);opacity:0}}
@keyframes rkuShackle{to{transform:translateY(-13px) rotate(32deg)}}
@keyframes rkuLockPop{30%{transform:translateY(-4px) scale(1.06)}100%{transform:translateY(0) scale(1)}}
@keyframes rkuSpark{0%{opacity:0;transform:translate(0,0) scale(0)}40%{opacity:1}100%{opacity:0;transform:translate(var(--dx),var(--dy)) scale(1)}}
`}</style>

                    <div className="relative">
                            <div
                                style={{
                                    animation:
                                        "rkuLockPop .5s ease .82s forwards",
                                }}
                            >
                                <svg
                                    width="76"
                                    height="82"
                                    viewBox="0 0 76 82"
                                    aria-hidden="true"
                                    style={{ overflow: "visible" }}
                                >
                                    <path
                                        d="M22 40 V26 a16 16 0 0 1 32 0 V40"
                                        fill="none"
                                        stroke="#cbb9a0"
                                        strokeWidth="7"
                                        strokeLinecap="round"
                                        style={{
                                            transformOrigin: "52px 34px",
                                            animation:
                                                "rkuShackle .5s cubic-bezier(.3,1.4,.5,1) .82s forwards",
                                        }}
                                    />
                                    <rect
                                        x="14"
                                        y="38"
                                        width="48"
                                        height="40"
                                        rx="9"
                                        fill="#e7d7bf"
                                    />
                                    <circle cx="38" cy="55" r="5.5" fill="#7a6a52" />
                                    <rect
                                        x="35.5"
                                        y="57"
                                        width="5"
                                        height="12"
                                        rx="2.5"
                                        fill="#7a6a52"
                                    />
                                </svg>
                            </div>
                            <div className="absolute left-1/2 top-1/2">
                                <img
                                    src="/static/icons/key.svg"
                                    alt=""
                                    className="h-[68px] w-[68px]"
                                    style={{
                                        transformOrigin: "9% 90%",
                                        animation:
                                            "rkuKey 1.7s cubic-bezier(.4,0,.2,1) forwards",
                                    }}
                                />
                            </div>
                            {SPARKS.map((sp, i) => (
                                <span
                                    key={i}
                                    className="absolute left-1/2 top-1/2 rounded-full"
                                    style={{
                                        width: sp.s,
                                        height: sp.s,
                                        background: i % 2 ? "#fff" : "#ffe49a",
                                        opacity: 0,
                                        "--dx": `${sp.dx}px`,
                                        "--dy": `${sp.dy}px`,
                                        animation:
                                            "rkuSpark .7s ease-out .9s forwards",
                                    }}
                                />
                            ))}
                        </div>

                    <div className="absolute right-4 top-4 flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 shadow-md">
                        <img
                            src="/static/icons/key.svg"
                            alt=""
                            className="h-4 w-4"
                        />
                        <span className="text-sm font-semibold text-gray-900">
                            {count}
                        </span>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
